import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings, resolveImageUrl } from "@/context/SettingsContext";
import { CoverField, ImageUploadButton } from "@/components/EventCover";
import { CalendarDays, MapPin, Plus, Trash2, UserPlus, Tv } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function EventsPage() {
  const { user } = useAuth();
  const { t } = useSettings();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setCover = async (ev, url) => {
    try {
      const { data } = await api.patch(`/events/${ev.event_id}`, { image_url: url });
      setEvents((list) => list.map((e) => e.event_id === ev.event_id ? { ...e, image_url: data.image_url } : e));
      toast.success(url ? "Cover updated." : "Cover removed.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? (d[0]?.msg || "Invalid input") : (d || "Could not update cover"));
    }
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    await api.delete(`/events/${ev.event_id}`);
    setEvents((list) => list.filter((e) => e.event_id !== ev.event_id));
    toast.success("Event removed.");
  };

  return (
    <div className="px-4 sm:px-8 py-10 sm:py-16 max-w-5xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.5em] uppercase text-crimson-bright mb-3">{t("events.eyebrow")}</div>
          <h1 className="font-display font-black tracking-[0.08em] uppercase text-3xl sm:text-5xl text-white">
            {t("events.title")} <span className="text-crimson">{t("events.accent")}</span>
          </h1>
        </div>
        {user?.role === "admin" && (
          <button
            data-testid="new-event-button"
            onClick={() => setShowForm((s) => !s)}
            className="btn-crimson px-4 py-2 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2"
          >
            <Plus size={14} /> {showForm ? "Cancel" : "New Event"}
          </button>
        )}
      </div>

      {showForm && user?.role === "admin" && (
        <EventForm
          onCreated={(ev) => { setEvents((l) => [...l, ev].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))); setShowForm(false); }}
        />
      )}

      {loading ? (
        <div className="text-silver text-sm tracking-widest uppercase animate-pulse">Loading quests...</div>
      ) : events.length === 0 ? (
        <div className="tavern-card p-10 text-center" data-testid="events-empty">
          <CalendarDays size={32} className="text-crimson-bright mx-auto mb-4" />
          <p className="text-zinc-400">{t("events.empty")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((ev, i) => (
            <article
              key={ev.event_id}
              data-testid={`event-card-${ev.event_id}`}
              className="tavern-card overflow-hidden flex flex-col sm:flex-row animate-fade-up group/card"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {ev.image_url && (
                <div className="relative sm:w-64 flex-shrink-0 h-44 sm:h-auto overflow-hidden bg-zinc-900">
                  <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-crimson/10 to-black" />
                  <img
                    src={resolveImageUrl(ev.image_url)}
                    alt={ev.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                    data-testid={`event-cover-${ev.event_id}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-transparent via-transparent to-black/60" />
                </div>
              )}
              <div className="flex-1 p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-6 min-w-0">
              <div className="flex-shrink-0 w-20 text-center border-r border-zinc-800 pr-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  {new Date(ev.starts_at).toLocaleString(undefined, { month: "short" })}
                </div>
                <div className="font-display font-black text-4xl text-crimson-bright leading-none mt-1">
                  {new Date(ev.starts_at).getDate()}
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
                  {new Date(ev.starts_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold tracking-wide uppercase text-lg sm:text-xl text-white">{ev.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 line-clamp-4 whitespace-pre-line">{ev.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} /> {formatDate(ev.starts_at)}</span>
                  {ev.location && (
                    <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {ev.location}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-3 flex-shrink-0">
                {(ev.signup_url || ev.watch_url) && (
                <div className="flex flex-wrap gap-2" data-testid={`event-actions-${ev.event_id}`}>
                {ev.signup_url && (
                  <a
                    href={ev.signup_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`signup-${ev.event_id}`}
                    className="btn-crimson px-4 py-2 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
                  >
                    <UserPlus size={14} /> Sign Up
                  </a>
                )}
                {ev.watch_url && (
                  <a
                    href={ev.watch_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`watch-${ev.event_id}`}
                    className="btn-ghost px-4 py-2 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
                  >
                    <Tv size={14} /> Watch Event
                  </a>
                )}
                </div>
                )}
                {user?.role === "admin" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <ImageUploadButton
                      testid={`event-cover-change-${ev.event_id}`}
                      label={ev.image_url ? "Cover" : "Add Cover"}
                      onUploaded={(url) => setCover(ev, url)}
                    />
                    {ev.image_url && (
                      <button
                        data-testid={`event-cover-clear-${ev.event_id}`}
                        onClick={() => setCover(ev, null)}
                        className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-crimson-bright"
                      >
                        Remove cover
                      </button>
                    )}
                    <button
                      data-testid={`delete-event-${ev.event_id}`}
                      onClick={() => removeEvent(ev)}
                      className="text-zinc-500 hover:text-crimson-bright p-2"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function EventForm({ onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", starts_at: "", location: "", image_url: "", signup_url: "", watch_url: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const okUrl = (v) => !v || /^https?:\/\//i.test(v.trim());
    if (!okUrl(form.signup_url) || !okUrl(form.watch_url)) {
      toast.error("Links must start with https://");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, signup_url: form.signup_url.trim(), watch_url: form.watch_url.trim(), image_url: form.image_url || null, starts_at: new Date(form.starts_at).toISOString() };
      const { data } = await api.post("/events", payload);
      onCreated(data);
      toast.success("Event posted to the scroll.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? (d[0]?.msg || "Invalid input") : (d || "Could not create event"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="tavern-card p-6 mb-8 grid gap-4 sm:grid-cols-2 animate-fade-up" data-testid="event-form">
      <div className="sm:col-span-2">
        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Title</label>
        <input required maxLength={120} className="tavern-input w-full px-3 py-2 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" />
      </div>
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Starts at</label>
        <input required type="datetime-local" className="tavern-input w-full px-3 py-2 rounded-sm" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-starts-at" />
      </div>
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Location</label>
        <input className="tavern-input w-full px-3 py-2 rounded-sm" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location" placeholder="Discord · The Library stage" />
      </div>
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Sign Up link (optional)</label>
        <input type="url" className="tavern-input w-full px-3 py-2 rounded-sm" value={form.signup_url} onChange={(e) => setForm({ ...form, signup_url: e.target.value })} data-testid="event-signup-url" placeholder="https://..." />
      </div>
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Watch Event link (optional)</label>
        <input type="url" className="tavern-input w-full px-3 py-2 rounded-sm" value={form.watch_url} onChange={(e) => setForm({ ...form, watch_url: e.target.value })} data-testid="event-watch-url" placeholder="https://twitch.tv/..." />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Description</label>
        <textarea required maxLength={2000} rows={4} className="tavern-input w-full px-3 py-2 rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description" />
      </div>
      <CoverField value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} />
      <div className="sm:col-span-2">
        <button type="submit" disabled={saving} className="btn-crimson px-5 py-2 text-xs tracking-[0.3em] uppercase disabled:opacity-60" data-testid="event-submit">
          {saving ? "Posting..." : "Post Event"}
        </button>
      </div>
    </form>
  );
}
